import React, { useEffect, useRef, useState, createElement } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { trackInitiateCheckout, trackLead } from '../../utils/metaPixel';
import { QualifyWizard, WizardSummary } from '../ui/QualifyWizard';
import { EMPTY_ANSWERS, loadAnswers, loadCompleted, type WizardAnswers } from '../../utils/wizardStorage';
const GHL_FORM_ID = 'ydIZJTgbcyUzjldi7fjM';
const GHL_FORM_URL = `https://api.leadconnectorhq.com/widget/form/${GHL_FORM_ID}`;

/**
 * PIXEL ARCHITECTURE — DermaClinic Lipo Center · Lipo Laser Beverly Hills
 *
 * The landing IS the funnel and owns every Meta event.
 * GHL only renders the form; its native pixel field is intentionally left
 * empty (per GHL's own guidance: "Ignore this field if you plan to use this
 * form/survey inside a funnel").
 *
 * QUALIFICATION WIZARD (DC-WA-FLOW-JL26)
 * A 5-step, tap-only wizard sits ABOVE the GHL form and reveals it on
 * completion. The wizard NEVER submits anything — GHL remains the single
 * source of leads, so there is no duplication and no inflated conversions.
 * Its answers are shown back as a summary so setters can confirm them during
 * the 2-minute callback.
 *
 * Landing-side events (Pixel 1575828340726848):
 *   - PageView         → App.tsx, on mount
 *   - ViewContent      → Benefits section in view
 *   - QualifyStep      → each wizard step (diagnostics, not a conversion)
 *   - QualifyComplete  → wizard finished, form revealed
 *   - InitiateCheckout → here, when the form section enters viewport
 *   - Lead             → here, on GHL form submission success postMessage
 *
 * The Lead listener is STRICT to prevent false positives:
 *   - Only fires on whitelisted exact submission-success signatures
 *   - Only fires ONCE per page load (one-shot guard)
 *   - Origin-checked to leadconnectorhq.com / msgsndr.com only
 *   - No substring matching (loose matching previously caused ~17x inflation
 *     vs real CRM submissions because lifecycle messages matched "submit")
 */
export function Contact() {
  const {
    t
  } = useLanguage();
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const sectionRef = useRef<HTMLElement | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const initiateFiredRef = useRef(false);
  const leadFiredRef = useRef(false);
  const [qualified, setQualified] = useState(false);
  const [answers, setAnswers] = useState<WizardAnswers>(EMPTY_ANSWERS);
  // A returning patient who already answered never sees the questions again
  useEffect(() => {
    if (!loadCompleted()) return;
    setAnswers(loadAnswers());
    setQualified(true);
  }, []);
  const handleQualified = (result: WizardAnswers) => {
    setAnswers(result);
    setQualified(true);
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 450);
  };
  useEffect(() => {
    // NOTE: GHL's form_embed.js is intentionally NOT loaded.
    // Its only job is auto-resizing the iframe, and it did that continuously
    // (on focus, validation, and field changes), making the form jump around
    // and pulling the patient's attention away mid-entry. We pin a fixed
    // height instead: the frame never moves, and the form scrolls internally
    // if it needs more room. Submission tracking does not depend on it.

    // Lead listener — strict whitelist of GHL success signatures
    const SUCCESS_SIGNATURES = ['form_submit_success', 'form:submit:success', 'form_submitted', 'formsubmitted', 'form-submit-success'];
    const isSuccessSignature = (value: unknown): boolean => {
      if (typeof value !== 'string') return false;
      return SUCCESS_SIGNATURES.includes(value.toLowerCase().trim());
    };
    const handleMessage = (event: MessageEvent) => {
      if (leadFiredRef.current) return;
      const origin = event.origin || '';
      if (!origin.includes('leadconnectorhq.com') && !origin.includes('msgsndr.com')) return;
      const data = event.data;
      // Debug log — exact payloads visible in DevTools console
      if (typeof console !== 'undefined') {
        console.debug('[GHL postMessage]', data);
      }
      let matched = false;
      if (typeof data === 'string') {
        matched = isSuccessSignature(data);
      } else if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        matched = isSuccessSignature(obj.type) || isSuccessSignature(obj.event) || isSuccessSignature(obj.eventName) || isSuccessSignature(obj.action) || obj.submitted === true;
      }
      if (matched) {
        leadFiredRef.current = true;
        trackLead({
          form_id: GHL_FORM_ID,
          source: 'landing_postmessage'
        });
      }
    };
    window.addEventListener('message', handleMessage);
    // 3) InitiateCheckout — fire when the section enters viewport (intent signal)
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initiateFiredRef.current) {
          initiateFiredRef.current = true;
          trackInitiateCheckout({
            form_id: GHL_FORM_ID
          });
        }
      });
    }, {
      threshold: 0.25
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      window.removeEventListener('message', handleMessage);
      observer.disconnect();
    };
  }, []);
  const benefits: string[] = t('contact.benefits');
  return <section id="contacto" ref={sectionRef} className="py-20 lg:py-28 bg-brand-dark text-brand-cream relative overflow-hidden" data-id="element-66" data-mp="1hfilvysyailn">
      {/* Subtle grain */}
      <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" data-id="element-67" data-mp="3j9hw0guc6twr"></div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-68" data-mp="3eb6my8bv8nfl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start max-w-6xl mx-auto" data-id="element-69" data-mp="1i70abve104a4">
          {/* Left column: persuasion */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: easeOutCubic
        }} className="lg:col-span-5 lg:pt-8 order-2 lg:order-1" data-id="element-70" data-mp="1n6mp499i5sl1">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-71" data-mp="262crhxlckqzg">
              <span className="w-8 h-px bg-brand-gold" data-id="element-72" data-mp="2avxqnsyofr08"></span>
              <span className="text-brand-gold/80 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-73" data-mp="2exiz9246hj4m">
                {t('contact.eyebrow')}
              </span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-serif font-light mb-6 leading-[1.05] tracking-tight" data-id="element-74" data-mp="2ouxgv51u8dyi">
              {t('contact.heading')}
            </h2>

            <p className="text-brand-cream/70 text-base lg:text-lg font-light leading-relaxed mb-8" data-id="element-75" data-mp="2z0f700oiediz">
              {t('contact.subhead')}
            </p>

            {/* Availability signal */}
            <div className="mb-10 pl-6 border-l border-brand-gold/40" data-id="element-76" data-mp="2p1g4z0y91g4y">
              <p className="font-serif italic text-brand-cream/60 text-sm leading-relaxed" data-id="element-77" data-mp="2l0e7opnw7e5m">
                {t('contact.urgency')}
              </p>
            </div>

            {/* Benefits list */}
            <ul className="space-y-4 mb-10" data-id="element-78" data-mp="3crk0sm574ble">
              {benefits.map((b, i) => <li key={i} className="flex items-start gap-4" data-id="element-79" data-mp="37z0n7p6idggl" data-mpi={i}>
                  <span className="text-brand-gold/70 font-serif italic text-sm shrink-0 mt-1" data-id="element-80" data-mp="37xfum53r87ld">
                    0{i + 1}
                  </span>
                  <span className="text-brand-cream/85 font-light leading-relaxed" data-id="element-81" data-mp="4bih574jn96c">
                    {b}
                  </span>
                </li>)}
            </ul>

            {/* Financing note — trust signals live beside the wizard action */}
            <div className="p-5 border border-brand-cream/15 rounded-2xl" data-id="element-82" data-mp="nyhgv8jw3xfc">
              <p className="text-[10px] font-medium tracking-widest uppercase text-brand-gold/80 mb-2" data-id="element-83" data-mp="o0kfui30t0w7">
                Financing
              </p>
              <p className="text-sm text-brand-cream/70 font-light leading-relaxed" data-id="element-84" data-mp="e9zgutjfpucr">
                {t('contact.financingNote')}
              </p>
            </div>
          </motion.div>

          {/* Right column: qualification wizard → GHL form */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: easeOutCubic
        }} id="calificar" className="lg:col-span-7 self-start space-y-5 order-1 lg:order-2 scroll-mt-24" data-id="element-85" data-mp="16v957weah2q9">
            {/* Step 1 — qualification wizard (tap-only, no lead submitted) */}
            <AnimatePresence mode="wait" data-id="element-86" data-mp="1uvb2obvoa1cg">
              {!qualified ? <motion.div key="wizard" initial={false} exit={{
              opacity: 0,
              y: -16
            }} transition={{
              duration: 0.35,
              ease: easeOutCubic
            }} data-id="element-87" data-mp="28rx0jmvjfq6v">
                  <QualifyWizard onComplete={handleQualified} data-id="element-88" data-mp="1pudpkdpirxht" />
                </motion.div> : <motion.div key="summary" initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              ease: easeOutCubic
            }} data-id="element-89" data-mp="1l2vlhgs79gsm">
                  <WizardSummary answers={answers} onEdit={() => setQualified(false)} data-id="element-90" data-mp="i3ue2fypnoos" />
                </motion.div>}
            </AnimatePresence>

            {/* Step 2 — the one and only lead capture: GHL form, untouched */}
            <AnimatePresence data-id="element-91" data-mp="454gz0bw5p0f">
              {qualified && <motion.div ref={formRef} initial={{
              opacity: 0,
              y: 24
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1,
              ease: easeOutCubic
            }} className="scroll-mt-24 rounded-[2rem] border border-brand-gold/30 bg-brand-cream shadow-2xl shadow-black/15" data-id="element-92" data-mp="3vhpbpa6wqpeo">
                  <div className="flex items-center gap-2.5 border-b border-brand-dark/10 px-6 py-4" data-id="element-93" data-mp="1x0f8yocrbano">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold text-[10px] font-semibold text-white" data-id="element-94" data-mp="21zrsaz4f5zli">
                      2
                    </span>
                    <span className="text-[11px] font-medium tracking-[0.15em] uppercase text-brand-dark/70" data-id="element-95" data-mp="19at5hopcq99v">
                      {t('wizard.stepper.details')}
                    </span>
                  </div>
                  {/* Fixed frame — no auto-resize, so the form never shifts
                      under the patient while they are typing. */}
                  <div className="h-[760px] sm:h-[820px] lg:h-[880px] w-full overflow-hidden rounded-b-[2rem]" data-id="element-96" data-mp="1n3slrswyk7ih">
                    <iframe src={GHL_FORM_URL} style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }} id={`inline-${GHL_FORM_ID}`} title="Form Los Angeles" data-id="element-97" data-mp="qi8ujm9lyouc" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>;
}